define((require) => {
    "use strict";

    const EditorEvents               = require("shared/services/EditorEvents"),
        ColorPicker                  = require("shared/services/ColorPicker"),
        OpacityPropertiesView        = require("ui/experience/widgets/OpacityPropertiesView"),
        template                     = require("ui/experience/widgets/StylePropertiesView.tpl"),
        AbstractWidgetPropertiesView = require("ui/experience/widgets/AbstractWidgetPropertiesView"),
        MathUtil                     = require("shared/util/MathUtil").default;

        // For the color picker
    var DEFAULT_COLOR = "#000000",
        // Applied when the different styles are toggled on
        DEFAULT_SHADOW = "rgb(0, 0, 0) 2px 2px 10px 0px";

    var EXPAND_PROPERTY = "styleExpanded";

    /**
     * The properties view for size and position information.
     *
     * @class SizeAndPositionPropertiesView
     * @constructor
     *
     * @cfg {AbstractWidgetModel} model The widget being edited.
     * @cfg {boolean} [hideSizeControls] If true, the size controls will be hidden.
     * @cfg {boolean} [hideRotation] If true, the rotation control will be hidden.
     * @cfg {boolean} [hideOpacityControls] If true, the opacity control will be hidden.
     */
    return AbstractWidgetPropertiesView.extend({

        expandProperty: EXPAND_PROPERTY,

        _borderColorPicker: null,

        template: template,

        regions: {
            opacityProperties: ".opacity-properties-region"
        },

        templateHelpers: function() {
            return {
                showBorderControls: !this.options.hideBorderControls,
                showOpacityControls: !this.options.hideOpacityControls,
                showRadiusControls: !this.options.hideRadiusControls
            };
        },

        events: {
            "shown.bs.collapse .style-properties-container": function() {
                this.viewScope.viewModel.get("widgetPropertyViewsState")[this.expandProperty] = true;
            },
            "hidden.bs.collapse .style-properties-container": function() {
                this.viewScope.viewModel.get("widgetPropertyViewsState")[this.expandProperty] = false;
            },
            "click .border": "_toggleBorderWidth",
            "click .opacity": "_toogleOpacity",
            "click .radius": "_toogleRadius"
        },

        ui: {
            container:              ".style-properties-container",
            legend:                 "[data-target=\".style-properties-container\"]",

            borderCheckBox:         ".border",
            borderProperties:       ".border-properties",
            borderWidth:            ".border-width",
            borderColorBox:         ".border-color-box",
            borderColorInput:       ".border-color-input",

            radiusCheckBox:         ".radius",
            radiusProperties:       ".radius-properties-region",

            opacityCheckBox:        ".opacity",
            opacityProperties:      ".opacity-properties-region"
        },

        bindings: {
            ".border": {
                observe: "borderWidth",
                onGet: function(value) {
                    return value > 0;
                },
                onSet: function(value) {
                    return value ? parseInt(this.ui.borderWidth.val(), 10) : null;
                }
            },
            ".border-width": {
                observe: "borderWidth",
                selectOptions: {
                    collection: [
                        {value: 1, label: "1px"},
                        {value: 2, label: "2px"},
                        {value: 3, label: "3px"},
                        {value: 5, label: "5px"},
                        {value: 10, label: "10px"}
                    ]
                }
            },
            ".border-color-input": "borderColor",
            ".shadow": {
                observe: "boxShadow",
                onGet: function(value) {
                    return !!value;
                },
                onSet: function(value) {
                    if (value) {
                        var viewModel = this.viewScope.viewModel,
                            experience = viewModel.get("experience"),
                            eventProperties = {
                                widgetType: this.model.get("type"),
                                updatedProperty: "style",
                                updatedValue: "shadow",
                                experienceId: experience.get("id"),
                                experienceVersion: experience.get("version"),
                                experiencePushedVersion: experience.get("pushedVersion"),
                                sceneNumber: viewModel.get("sceneNumber"),
                                triggerOrigin: "WidgetMenuStyleCheckbox"
                            };

                        EditorEvents.trigger(EditorEvents.constants.UPDATE_WIDGET, eventProperties);
                    }
                    return value ? DEFAULT_SHADOW : null;
                }
            },
            ".border-radius-input": {
                observe: "radius",
                onGet: function(value) {
                    return value || 0;
                },
                onSet: function(value) {
                    return parseInt(value, 10) || 0;
                }
            }
        },

        onRender: function() {
            this.__super();

            if (!this.options.hideBorderControls) {
                this._borderColorPicker = new ColorPicker({
                    color: this.model.get("borderColor") || DEFAULT_COLOR,
                    input: this.ui.borderColorInput,
                    colorBox: this.ui.borderColorBox
                });
            }

            if (!this.options.hideRadiusControls) {
                if (this.model.get("radius")) {
                    this.ui.radiusCheckBox.prop("checked", true);
                    this.model.get("radius");
                } else {
                    this.ui.radiusProperties.hide();
                }
            }

            if (!this.options.hideOpacityControls) {
                this.opacityProperties.show(new OpacityPropertiesView({model: this.model, hideName: true}));

                if (this.model.get("opacity") !== 1) {
                    this.ui.opacityCheckBox.prop("checked", true);
                } else {
                    this.ui.opacityProperties.hide();
                }
            }

            this.ui.borderProperties.toggle(this.model.has("borderWidth"));

            this.listenTo(this.model, "change:borderWidth", this._repositionAndResizeWidget.bind(this));
        },

        // This method is called after border of widget has been chaged
        //
        // The rounding here helps not to lost all the precision changes
        // to the integer pixels contain rounding
        // in other words: removes the redundant accuracy
        //
        // TODO: Fix the border adding to widget width here
        // https://jira.zmags.com/browse/DEV-2573
        _repositionAndResizeWidget: function() {
            var from = this.model.previousAttributes().borderWidth || 0,
                to = this.model.get("borderWidth") || 0,
                diff = to - from;

            // if for whatever reason we aren't getting numbers out of the above we cancel the
            // operation
            if (typeof diff !== "number") {
                return;
            }

            var experienceModel = this.viewScope.viewModel.get("experience"),
                diffSize = experienceModel.getPercentageSize({width: diff, height: diff});
            // reposition widget so top left corner doesn't change position
            this.model.set({
                left: this.model.get("left") - MathUtil.roundToContain(diffSize.width, experienceModel.get("width")),
                top: this.model.get("top") - MathUtil.roundToContain(diffSize.height, experienceModel.get("height"))
            });

            if (this.model.has("width") && this.model.has("height")) {
                // resize widget (to adjust for the new border width)
                this.model.set({
                    width: this.model.get("width") + 2 * MathUtil.roundToContain(diffSize.width, experienceModel.get("width")),
                    height: this.model.get("height") + 2 * MathUtil.roundToContain(diffSize.height, experienceModel.get("height"))
                });
            }
        },

        onClose: function() {
            // Custom color picker cleanup
            if (this._borderColorPicker) {
                this._borderColorPicker.dispose();
            }
        },

        _toggleBorderWidth: function() {
            if (this.ui.borderCheckBox.is(":checked")) {
                this.ui.borderProperties.slideDown();

                var viewModel = this.viewScope.viewModel,
                    experience = viewModel.get("experience"),
                    eventProperties = {
                        widgetType: this.model.get("type"),
                        updatedProperty: "style",
                        updatedValue: "stroke",
                        experienceId: experience.get("id"),
                        experienceVersion: experience.get("version"),
                        experiencePushedVersion: experience.get("pushedVersion"),
                        sceneNumber: viewModel.get("sceneNumber"),
                        triggerOrigin: "WidgetMenuStyleCheckbox"
                    };

                EditorEvents.trigger(EditorEvents.constants.UPDATE_WIDGET, eventProperties);
            } else {
                this.ui.borderProperties.slideUp();
            }
        },

        _toogleRadius: function() {
            if (this.ui.radiusCheckBox.is(":checked")) {
                this.ui.radiusProperties.slideDown();

                var viewModel = this.viewScope.viewModel,
                    experience = viewModel.get("experience"),
                    eventProperties = {
                        widgetType: this.model.get("type"),
                        updatedProperty: "style",
                        updatedValue: "radius",
                        experienceId: experience.get("id"),
                        experienceVersion: experience.get("version"),
                        experiencePushedVersion: experience.get("pushedVersion"),
                        sceneNumber: viewModel.get("sceneNumber"),
                        triggerOrigin: "WidgetMenuStyleCheckbox"
                    };

                EditorEvents.trigger(EditorEvents.constants.UPDATE_WIDGET, eventProperties);
            } else {
                this.ui.radiusProperties.slideUp();
                this.model.set("radius", 0);
            }
        },

        _toogleOpacity: function() {
            if (this.ui.opacityCheckBox.is(":checked")) {
                this.ui.opacityProperties.slideDown();

                var viewModel = this.viewScope.viewModel,
                    experience = viewModel.get("experience"),
                    eventProperties = {
                        widgetType: this.model.get("type"),
                        updatedProperty: "style",
                        updatedValue: "opacity",
                        experienceId: experience.get("id"),
                        experienceVersion: experience.get("version"),
                        experiencePushedVersion: experience.get("pushedVersion"),
                        sceneNumber: viewModel.get("sceneNumber"),
                        triggerOrigin: "WidgetMenuStyleCheckbox"
                    };

                EditorEvents.trigger(EditorEvents.constants.UPDATE_WIDGET, eventProperties);
            } else {
                this.ui.opacityProperties.slideUp();
                this.model.set("opacity", 1);
            }
        }
    }, {
        EXPAND_PROPERTY
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/StylePropertiesView.js